import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/pages/home/components/Footer';
import { allProducts } from '@/mocks/products';

interface CartItem {
  id: number;
  name: string;
  price: number;
  originalPrice: number;
  image: string;
  category: string;
  quantity: number;
}

const initialCart: CartItem[] = [];

export default function CartPage() {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState<CartItem[]>(() => {
    try {
      const raw = localStorage.getItem('cart');
      if (raw) {
        const parsed = JSON.parse(raw) as CartItem[];
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch {
      // fall through to initial
    }
    return initialCart;
  });

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cartItems));
  }, [cartItems]);

  const updateQty = (id: number, delta: number) => {
    setCartItems(items =>
      items.map(item =>
        item.id === id ? { ...item, quantity: Math.max(1, item.quantity + delta) } : item
      )
    );
  };

  const removeItem = (id: number) => {
    setCartItems(items => items.filter(item => item.id !== id));
  };

  const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const deliveryFee = subtotal >= 200 ? 0 : 25;
  const total = subtotal + deliveryFee;

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50/30 via-white to-pink-50/30">
        <Header />
        <div className="pt-32 pb-20 flex flex-col items-center justify-center px-6">
          <div className="w-32 h-32 flex items-center justify-center rounded-full bg-orange-50 mb-6 animate-bounceIn">
            <i className="ri-shopping-cart-line text-6xl text-orange-400"></i>
          </div>
          <h2 className="text-3xl font-black text-gray-800 mb-3">Your cart is empty</h2>
          <p className="text-gray-500 mb-8 text-center max-w-md">Looks like you haven&apos;t added anything yet. Explore our amazing products!</p>
          <Link
            to="/shop"
            className="px-8 py-4 rounded-full bg-gradient-to-r from-orange-500 to-pink-600 text-white font-bold hover:scale-105 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-store-line mr-2"></i>Start Shopping
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50/30 via-white to-pink-50/30">
      <Header />

      <div className="pt-24 pb-4 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-2xl sm:text-3xl font-black bg-gradient-to-r from-orange-500 to-pink-600 bg-clip-text text-transparent mb-1">
            Shopping Cart
          </h1>
          <p className="text-sm text-gray-500">
            <Link to="/" className="hover:text-orange-500 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line mx-1"></i>
            <span className="text-gray-800">Cart ({cartItems.length} items)</span>
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cartItems.map(item => {
              const disc = Math.round(((item.originalPrice - item.price) / item.originalPrice) * 100);
              return (
                <div key={item.id} className="bg-white/70 backdrop-blur-sm rounded-3xl border border-gray-100/50 p-3 sm:p-4 flex gap-3 sm:gap-4 group hover:border-orange-200 transition-all duration-300">
                  <Link to={`/product/${item.id}`} className="w-16 h-16 sm:w-24 sm:h-24 flex-shrink-0 rounded-2xl overflow-hidden">
                    <img src={item.image} alt={item.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-xs text-orange-500 font-medium capitalize">{item.category}</p>
                        <Link to={`/product/${item.id}`} className="font-bold text-gray-800 text-xs sm:text-sm hover:text-orange-500 transition-colors line-clamp-2">
                          {item.name}
                        </Link>
                      </div>
                      <button
                        onClick={() => removeItem(item.id)}
                        className="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-red-50 hover:text-red-500 transition-all cursor-pointer flex-shrink-0"
                      >
                        <i className="ri-delete-bin-line text-sm"></i>
                      </button>
                    </div>
                    <div className="flex items-center justify-between mt-2 sm:mt-3">
                      <div className="flex items-center border border-gray-200 rounded-full overflow-hidden">
                        <button
                          onClick={() => updateQty(item.id, -1)}
                          className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center text-gray-600 hover:bg-orange-50 hover:text-orange-500 transition-all cursor-pointer"
                        >
                          <i className="ri-subtract-line text-sm"></i>
                        </button>
                        <span className="w-7 sm:w-8 text-center text-sm font-bold text-gray-800">{item.quantity}</span>
                        <button
                          onClick={() => updateQty(item.id, 1)}
                          className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center text-gray-600 hover:bg-orange-50 hover:text-orange-500 transition-all cursor-pointer"
                        >
                          <i className="ri-add-line text-sm"></i>
                        </button>
                      </div>
                      <div className="text-right">
                        <p className="font-black text-gray-800 text-sm sm:text-base">AED {item.price * item.quantity}</p>
                        <p className="text-xs text-gray-400 line-through">AED {item.originalPrice * item.quantity}</p>
                        <p className="text-xs text-green-600 font-semibold">-{disc}%</p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Continue Shopping */}
            <Link
              to="/shop"
              className="flex items-center gap-2 text-orange-500 font-semibold hover:text-orange-600 transition-colors cursor-pointer text-sm"
            >
              <i className="ri-arrow-left-line"></i>
              Continue Shopping
            </Link>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white/70 backdrop-blur-sm rounded-3xl border border-gray-100/50 p-6 sticky top-24">
              <h2 className="text-xl font-black text-gray-800 mb-6">Order Summary</h2>

              {/* Price Breakdown */}
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Subtotal ({cartItems.reduce((s, i) => s + i.quantity, 0)} items)</span>
                  <span className="font-semibold text-gray-800">AED {subtotal}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Delivery</span>
                  <span className={`font-semibold ${deliveryFee === 0 ? 'text-green-600' : 'text-gray-800'}`}>
                    {deliveryFee === 0 ? 'FREE' : `AED ${deliveryFee}`}
                  </span>
                </div>
                <div className="border-t border-gray-100 pt-3 flex justify-between">
                  <span className="font-black text-gray-800">Total</span>
                  <span className="font-black text-xl bg-gradient-to-r from-orange-500 to-pink-600 bg-clip-text text-transparent">
                    AED {total}
                  </span>
                </div>
              </div>

              <button
                onClick={() => navigate('/checkout')}
                className="w-full py-4 rounded-full bg-gradient-to-r from-orange-500 to-pink-600 text-white font-bold hover:scale-[1.02] transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-secure-payment-line mr-2"></i>
                Proceed to Checkout
              </button>

              {/* Trust */}
              <div className="flex items-center justify-center gap-4 mt-4">
                {['ri-shield-check-line', 'ri-lock-line', 'ri-truck-line'].map((icon, i) => (
                  <div key={i} className="w-8 h-8 flex items-center justify-center text-gray-400">
                    <i className={`${icon} text-lg`}></i>
                  </div>
                ))}
              </div>
              <p className="text-center text-xs text-gray-400 mt-1">Secure checkout • Free returns • UAE delivery</p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
